import React, { useMemo } from 'react';

import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { IModalAttachment } from '@entities/attachment/model/IModalAttachment.interface';
import { DeleteConfirm } from '@features/FilePreviewer/ui/DeleteConfirm';
import { TextEditor } from '@shared/ui/TextEditor/TextEditor';
import { IAddModalSchemaState } from 'entities/modal/model/types/addModalSchema';
import { FieldErrors } from 'entities/modal/model/types/modalFieldErrors';
import {
    ColorPicker,
    ExecutorsStaff,
    FilePreviewer,
    ModalDatePicker,
    ModalFilesUploader,
    MultiSelectTags,
    ObjectsOrEstimatesSelect,
    ObserversStaff,
    TaskStatusSwitcherBlock,
} from 'features';
import { TModalMode } from 'shared/model/types/modalMode.type';
import { CustomModal, Flex, ModalTitle, RadioBtn, UiButton } from 'shared/ui';

import cls from './ModalAddNewTask.module.css';
import { Loader } from '../../../../components/UI/NewLoader/Loader/Loader';
import { ModalContext } from '../../model/context';

interface ModalAddNewTaskViewProps {
    attachments: IModalAttachment[];
    deleteConfirmModal: boolean;
    fieldErrors: FieldErrors;
    fileIdToDeleteFromList: string | null;
    fileIdToPreview: string | null;
    isCheckedRadio: boolean;
    isModalLoading: boolean;
    modalMode: TModalMode | null;
    modalState: IAddModalSchemaState;
    portalFileOverlays?: boolean;
    previewEnable: boolean;
    smetterCssClassName?: string;
    triggerAnimationError: boolean;
    deleteAttachment: (fileId: string | undefined) => void;
    handleRadioBtn: () => void;
    onChangeModalDescription: (description: string) => void;
    onChangeModalTitle: (title: string) => void;
    onEnablePreviewModal: (enablePreview: boolean, fileId: string) => void;
    onHideModal: () => void;
    onHidePrevModal: () => void;
    onSubmit: () => Promise<void>;
    setAttachments: React.Dispatch<React.SetStateAction<IModalAttachment[]>>;
    setDeleteConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
    setFieldErrors: React.Dispatch<React.SetStateAction<FieldErrors>>;
    setFileIdToDeleteFromList: React.Dispatch<React.SetStateAction<string | null>>;
    setTriggerAnimationError: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ModalAddNewTaskView = ({
    attachments,
    deleteConfirmModal,
    fieldErrors,
    fileIdToDeleteFromList,
    fileIdToPreview,
    isCheckedRadio,
    isModalLoading,
    modalMode,
    modalState,
    portalFileOverlays = false,
    previewEnable,
    smetterCssClassName,
    triggerAnimationError,
    deleteAttachment,
    handleRadioBtn,
    onChangeModalDescription,
    onChangeModalTitle,
    onEnablePreviewModal,
    onHideModal,
    onHidePrevModal,
    onSubmit,
    setAttachments,
    setDeleteConfirmModal,
    setFieldErrors,
    setFileIdToDeleteFromList,
    setTriggerAnimationError,
}: ModalAddNewTaskViewProps) => {
    const { t } = useTranslation();
    const modalContextValue = useMemo(
        () => ({
            attachments,
        }),
        [attachments]
    );
    const fileOverlays = (
        <>
            {deleteConfirmModal && (
                <DeleteConfirm
                    onHideModal={() => setDeleteConfirmModal(false)}
                    onDeleteFile={deleteAttachment}
                    fileId={fileIdToDeleteFromList}
                />
            )}
            <FilePreviewer
                filesToPreview={attachments}
                isOpen={previewEnable}
                onHide={onHidePrevModal}
                fileIdToPreview={fileIdToPreview}
                onDeleteFile={deleteAttachment}
            />
        </>
    );

    return (
        <ModalContext.Provider value={modalContextValue}>
            {portalFileOverlays ? createPortal(fileOverlays, document.body) : fileOverlays}
            <CustomModal
                isOpen={modalMode === 'addNewTask'}
                onHideModal={onHideModal}
                classes={smetterCssClassName}
                contentClasses={cls.ModalContainerWidth}
            >
                {isModalLoading && <Loader variant="light" />}
                <CustomModal.Header classes={cls.ModalHeader}>
                    <div className={classNames(cls.ModalHeaderWrapper)}>
                        <div className={cls.TitleWrapper}>
                            <RadioBtn
                                isChecked={isCheckedRadio}
                                onClickHandler={handleRadioBtn}
                                fill={modalState.title.length === 0 ? '#ADB0B5' : undefined}
                            />
                            <ModalTitle
                                autoFocus
                                onChangeHandler={(e) => onChangeModalTitle(e.target.value)}
                                placeholder={t('TaskModal.ModalTitle.placeholder')}
                                classes={classNames(isCheckedRadio ? cls.checkedTaskTitle : '')}
                                value={modalState.title}
                                isError={fieldErrors.title.isError}
                                triggerAnimationError={triggerAnimationError}
                                setTriggerAnimationError={setTriggerAnimationError}
                            />
                        </div>
                        <TaskStatusSwitcherBlock isDisabled={isCheckedRadio} />
                    </div>
                </CustomModal.Header>
                <CustomModal.Body classes={cls.ModalBody}>
                    <div className={cls.Container}>
                        <TextEditor
                            inputName="text-editor"
                            value={modalState.description}
                            onChange={onChangeModalDescription}
                            placeholder={t('TaskModal.TextEditor.placeholder')}
                            readOnly={isCheckedRadio || isModalLoading}
                            maxLength={2000}
                            isDisabled={isCheckedRadio}
                        />
                        <ModalFilesUploader
                            modalMode={modalMode}
                            attachments={attachments}
                            setAttachments={setAttachments}
                            setFileIdToDeleteFromList={setFileIdToDeleteFromList}
                            onEnablePreviewModal={onEnablePreviewModal}
                            setDeleteConfirmModal={setDeleteConfirmModal}
                            previewEnable={previewEnable}
                            isDisabled={
                                isCheckedRadio || isModalLoading || attachments.length >= 10
                            }
                            isTaskChecked={isCheckedRadio}
                        />
                    </div>
                    <div className={cls.Container}>
                        <div className={cls.SelectedTagsContainer}>
                            <MultiSelectTags
                                modalMode={modalMode}
                                isDisabled={isCheckedRadio || isModalLoading}
                                error={fieldErrors.tags}
                                setFieldErrors={setFieldErrors}
                            />
                        </div>
                        <ColorPicker
                            isDisabled={isCheckedRadio || isModalLoading}
                            modalMode={modalMode}
                        />
                        <div>
                            <ModalDatePicker
                                modalMode={modalMode}
                                isCheckedRadio={isCheckedRadio}
                                isModalLoading={isModalLoading}
                                fieldErrors={fieldErrors}
                                setFieldErrors={setFieldErrors}
                            />
                        </div>
                    </div>
                    <ObjectsOrEstimatesSelect
                        isDisabled={isCheckedRadio || isModalLoading}
                        modalMode={modalMode}
                        error={fieldErrors.objectOrEstimate}
                        setFieldErrors={setFieldErrors}
                    />
                    <div className={cls.RolesWrapper}>
                        <ExecutorsStaff
                            text={t('Tasks.ExecutorsStaff.title')}
                            modalMode={modalMode}
                            isDisabled={isCheckedRadio || isModalLoading}
                            error={fieldErrors.executor}
                            setFieldErrors={setFieldErrors}
                            highestRole="author"
                        />
                        <ObserversStaff
                            text={t('Tasks.ObserversStaff.title')}
                            modalMode={modalMode}
                            isDisabled={isCheckedRadio || isModalLoading}
                            highestRole="author"
                        />
                    </div>
                </CustomModal.Body>

                <CustomModal.Footer classes={cls.AddModalFooter}>
                    <Flex fullWidth justify="between">
                        <UiButton
                            btnColor="secondary"
                            btnText={t('ModalAddNewTask.CancelBtn.text')}
                            onClick={onHideModal}
                        />
                        <UiButton
                            btnText={t('ModalAddNewTask.CreateTaskBtn.text')}
                            onClick={onSubmit}
                            disabled={
                                isModalLoading ||
                                fieldErrors.date.isError ||
                                fieldErrors.tags.isError ||
                                fieldErrors.title.isError ||
                                fieldErrors.description.isError ||
                                fieldErrors.executor.isError ||
                                fieldErrors.objectOrEstimate.isError
                            }
                        />
                    </Flex>
                </CustomModal.Footer>
            </CustomModal>
        </ModalContext.Provider>
    );
};
